import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'videojs-vr/dist/videojs-vr.css';
import 'videojs-vr/dist/videojs-vr';
import { useParams } from 'react-router';
import { PropTypes } from 'prop-types';
import { usePageVisibility } from '../../../hooks/usePageVisibility';
import useLocales from '../../../hooks/useLocales';
import Iconify from '../../Iconify';

VRVideoViewer.propTypes = {
  preview: PropTypes.any,
};
const skTranslate = {
  'Play Video': 'Prehrať video',
  Play: 'Prehrať ',
  Pause: 'Pauza',
  Mute: 'Stlmiť',
  'Current Time': 'Aktuálny čas',
  Duration: 'Trvanie',
  'Remaining Time': 'Zostávajúci čas',
  'subtitles settings': 'Nastavenia',
  'Picture-in-Picture': 'Obraz v obraze',
  Fullscreen: 'Celá obrazovka',
  'Playback Rate': 'Rýchlosť prehrávania',
  Background: 'Pozadie',
  Window: 'Okno',
  'Font Size': 'Veľkosť fontu',
  'Text Edge Style': 'Štýl okrajov textu',
  'Font Family': 'Font',
  Reset: 'Obnoviť',
  Done: 'Uložiť',
  Opaque: 'Nepriehľadné',
  'Semi-Transparent': 'Polo priehľadné',
  Transparent: 'Priehľadné',
  None: 'Žiadny',
  Raised: 'Zvýšený',
  Depressed: 'Znížený',
  Uniform: 'Jednotný',
  Dropshadow: 'S Tieňom',
  'Stream Type': 'Typ streamu',
  LIVE: 'NAŽIVO',
  Loaded: 'Načítaný ',
  Progress: 'Progres ',
  'Non-Fullscreen': 'Zmenšiť',
  Unmute: 'Zapnúť zvuk',
  Subtitles: 'Titulky',
  'subtitles off': 'Vypnúť titulky',
  // Captions: 'Subtítulos especiales',
  // 'captions off': 'Subtítulos especiales desactivados',
  Chapters: 'Kapitoly',
  'Close Modal Dialog': 'Zatvorte dialógové modalne okno ',
  'You aborted the video playback': 'Prerušili ste prehrávanie videa',
  'A network error caused the video download to fail part-way.':
    'Chyba siete spôsobila čiastočné zlyhanie sťahovania videa.',
  'The video could not be loaded, either because the server or network failed or because the format is not supported.':
    'Video sa nepodarilo načítať, buď preto, že zlyhal server alebo sieť, alebo preto, že formát nie je podporovaný.',
  'The video playback was aborted due to a corruption problem or because the video used features your browser did not support.':
    'Prehrávanie videa bolo prerušené pre problém s poškodením alebo preto, že video používalo funkcie, ktoré váš prehliadač nepodporoval.',
  'No compatible source was found for this video.': 'Pre toto video sa nenašiel žiadny kompatibilný zdroj.',
};
export default function VRVideoViewer({ preview }) {
  // temporary till subtitles are added to the database
  const { id } = useParams();
  const {
    currentLang: { value },
    translate,
  } = useLocales();

  const isVisible = usePageVisibility();
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [options, setOptions] = useState({
    autoplay: false,
    controls: true,
    fluid: true,
    language: value,
    // height: 450,
    preload: true,
    controlBar: {
      liveDisplay: true,
      pictureInPictureToggle: false,
    },
    html5: {
      nativeTextTracks: false,
      featuresNativeTextTracks: true,
    },
    // tracks: getTracks(id),
    poster: preview?.thumbnail?.fileUrl,
    playbackRates: [0.5, 1, 1.5, 2],
    sources: preview?.videos?.map((video) => ({
      src: video.fileUrl,
      type: 'video/mp4',
    })),
    vr: {
      projection: 'sphere',
      debug: false,
      sphereDetail: 32,
    },
  });

  useEffect(() => {
    if (!isVisible) {
      if (playerRef.current) {
        const player = playerRef.current;

        player.pause();
      }
    }
  }, [isVisible, playerRef]);

  useEffect(() => {
    setOptions({
      autoplay: false,
      controls: true,
      fluid: true,
      language: value,
      // height: 450,
      html5: {
        nativeTextTracks: false,
        featuresNativeTextTracks: true,
      },
      preload: true,
      controlBar: {
        liveDisplay: true,
        pictureInPictureToggle: false,
      },
      poster: preview?.thumbnail?.fileUrl,
      playbackRates: [0.5, 1, 1.5, 2],
      // tracks: getTracks(id),

      sources: preview?.videos?.map((video) => ({
        src: video.fileUrl,
        type: 'video/mp4',
      })),
      vr: {
        projection: 'sphere',
        debug: false,
        sphereDetail: 32,
      },
    });
  }, [id, preview, value]);

  useEffect(() => {
    if (value === 'sk') {
      videojs.addLanguage('sk', skTranslate);
    }
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      videoElement.setAttribute('playsinline', '');
      videoElement.crossOrigin = 'anonymous';

      if (videoRef.current !== null) {
        videoRef.current.appendChild(videoElement);
        playerRef.current = videojs(videoElement, options, () => {
          const player = playerRef.current;
          player.vr({ projection: 'sphere', debug: true, sphereDetail: 320 }); //
        });
      }
    } else {
      const player = playerRef.current;
      player.language(value);
      player.autoplay(options.autoplay);
    }
  }, [options, videoRef, value]);

  const isMozilla = typeof InstallTrigger !== 'undefined';

  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  const el = document.getElementById('vjs_video_3');
  if (el) {
    el.style.paddingTop = '53.25%';
  }

  if (isMozilla) {
    return (
      <div className="preview-box">
        <span className="preview-loading">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center', // Add this line to align the content vertically
              flexDirection: 'column',
              textAlign: 'center',
              lineHeight: '1.3',
            }}
          >
            <Iconify
              icon="iconoir:pc-warning"
              color="#ffc425"
              width={44}
              height={44}
              sx={{ marginBottom: '24px !important' }}
            />
            <Box sx={{ width: '85%' }} dangerouslySetInnerHTML={{ __html: translate('default.vrFirefoxALert') }} />
          </Box>
        </span>
      </div>
    );
  }

  return (
    <div data-vjs-player>
      <div ref={videoRef} />
    </div>
  );
}
